import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { FormInstance } from 'antd/es/form/Form';
import { useTranslation } from 'react-i18next';
import { Logins, getPossibleLogins } from '../helpers/getPossibleLogins';
import { SelectedLogin } from '../components/PossibleLogins/PossibleLogins';
import { EmailFormInstance } from '../components/LoginFlow/EmailForm';
import { useAltaLoginUrlParams } from './useAltaLoginUrlParams';
import { HandleIdpSsoLoginArgs } from '../components/UnifiedLogin/UnifiedLogin';

interface UseGetLoginsForEmailIOptions {
  emailForm: FormInstance<EmailFormInstance>;
  setLogins: Dispatch<SetStateAction<Logins[] | undefined>>;
  setSelectedLogin(value: SelectedLogin): void;
  handleIdpSsoLogin(args: HandleIdpSsoLoginArgs): void;
}

/**
 * @description hook that sets up the ability to process an email, org id, region,
 * and service to gather all possible logins for the current state.
 * @returns {
 *  getLoginsForEmail: handler search for logins.
 *  isSubmittingEmail: flag to mark if we are currently searching for logins
 * }
 */
export const useGetLoginsForEmail = ({
  emailForm,
  setSelectedLogin,
  setLogins,
  handleIdpSsoLogin,
}: UseGetLoginsForEmailIOptions) => {
  const { t } = useTranslation();
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);

  const { orgId, service, region } = useAltaLoginUrlParams();
  const isDmp = useMemo(() => Boolean(service && service === 'dmp'), [service]);
  /**
   * @description function that finds the allowed namespaces to login to for the given options.
   *  if no allowed namespaces are found, we set a form error.
   *  if only 1 allowed namespace is found, we set that namespace as selected.
   *  if more than 1 allowed namespace is found, we allow the user to choose the namespace.
   * @returns void
   */
  const getLoginsForEmail = useCallback(
    async (chosenEmail: string) => {
      try {
        setIsSubmittingEmail(true);

        const possibleLogins = await getPossibleLogins(chosenEmail, {
          env: region,
          orgId: orgId ? Number(orgId) : undefined,
          isDmp,
        });

        // More than one found, user needs to choose which one
        if (
          possibleLogins.length > 1 ||
          possibleLogins[0]?.namespaces.length > 1
        ) {
          setIsSubmittingEmail(false);
          setLogins(possibleLogins);
          return;
        }

        // Only one found, redirect to control center
        if (possibleLogins.length === 1) {
          // Only one SSO enabled IDP found, initiate SSO login process
          if (
            possibleLogins[0].namespaces[0].namespaceType?.modelName ===
              'identityProvider' &&
            possibleLogins[0].namespaces[0].identityProvider?.isSsoEnabled
          ) {
            return handleIdpSsoLogin({
              email: chosenEmail,
              env: possibleLogins[0].env,
              namespaceNickname: possibleLogins[0].namespaces[0].nickname!,
              onError: () => {
                setIsSubmittingEmail(false);
              },
            });
          }
          setIsSubmittingEmail(false);
          setSelectedLogin({
            regionEnv: possibleLogins[0].env,
            heliumEndpoint: possibleLogins[0].helium?.endpoint!,
            namespace: possibleLogins[0].namespaces[0],
          });

          return;
        }

        setIsSubmittingEmail(false);

        emailForm.setFields([
          {
            name: 'email',
            errors: [t("Couldn't find your account")],
          },
        ]);
      } catch {
        setIsSubmittingEmail(false);
      }
    },
    [
      region,
      orgId,
      isDmp,
      emailForm,
      t,
      setLogins,
      setSelectedLogin,
      handleIdpSsoLogin,
    ],
  );

  return { getLoginsForEmail, isSubmittingEmail };
};
